import React, { createContext, useState, useEffect, useContext } from 'react';
import commonApi from '../utils/api';
import { CommonContext } from './CommonContext';
import { format } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';

export const SubCategoryContext = createContext();

const initialFilters = {
    searchBy: 'name',
    searchValue: '',
    status: '',
    merchant_id: [],
    dateRange: {
        startDate: moment().subtract(1, 'month').toDate(),
        endDate: moment().add(0, 'month').toDate(),
    },
};

export function SubCategoryContextProvider({ children }) {
    const { user, setModalAlert, setOpenAlert, setAlertMessage, setSeverity } = useContext(CommonContext);
    const navigate = useNavigate();

    const [addModal, setAddModal] = useState(false);
    const [allData, setAllData] = useState([]);
    const [allSubCategories, setAllSubCategories] = useState([]);
    const [viewData, setViewData] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [isLoading, setIsLoading] = useState(false);
    const [firstLoading, setFirstLoading] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selected, setSelected] = useState([]);
    const [filters, setFilters] = useState(initialFilters);
    const [locationState, setLocationState] = useState(null);
    const [uploadModal, setUploadModal] = useState(false);

    const location = useLocation();

    const handleAddModal = () => {
        setAddModal(true);
        setModalAlert(false);
        setOpenAlert(false);
        setAlertMessage('');
        setSeverity('');
    };

    const handleCloseModal = () => {
        setViewData('')
        setIsEdit(false);
        setAddModal(false);
    };

    const handleUploadModal = () => {
        setUploadModal(true);
    };

    const handleCloseUploadModal = () => {
        setUploadModal(false);
    };

    const onLoadApi = (customFilters = filters) => {
        let payload = {
            ...customFilters,
            start_date: customFilters?.dateRange?.startDate
                ? format(new Date(customFilters.dateRange.startDate), 'yyyy-MM-dd')
                : null,
            end_date: customFilters?.dateRange?.endDate
                ? format(new Date(customFilters.dateRange.endDate), 'yyyy-MM-dd')
                : null,
        };

        if (user?.user_type === "Merchant") {
            payload.merchant_id = user?.id;
        } else {
            if (customFilters.merchant_id && customFilters.merchant_id.length > 0) {
                payload.merchant_id = customFilters.merchant_id;
            } else if (location?.state) {
                payload.merchant_id = location.state.id;
                setLocationState(location.state);
            } else {
                payload.admin_id = user?.id;
            }
        }

        delete payload.dateRange;
        setAllSubCategories([]);
        setAllData([]);
        setFirstLoading(false);
        commonApi.post(`/merchant-subcategory/list?page=${page + 1}&per_page=${rowsPerPage}`, payload)
            .then((response) => {
                setFirstLoading(true);
                if (response?.data?.data) {
                    setAllSubCategories(response?.data?.data);
                    setAllData(response?.data);
                }
            })
            .catch((error) => {
                setFirstLoading(true);
                console.error('Error fetching data:', error);
            });
    };

    const viewDetail = (id) => {
        setIsLoading(true);
        commonApi.post('/merchant-subcategory/view', { id: id })
            .then((response) => {
                setIsLoading(false);
                if (response?.data?.data) {
                    setIsEdit(true);
                    setViewData(response?.data?.data);
                    handleAddModal();
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error('Error fetching data:', error);
            });
    }

    const onExport = (customFilters = filters) => {
        const payload = {
            ...customFilters,
            start_date: customFilters?.dateRange?.startDate
                ? format(new Date(customFilters.dateRange.startDate), 'yyyy-MM-dd')
                : null,
            end_date: customFilters?.dateRange?.endDate
                ? format(new Date(customFilters.dateRange.endDate), 'yyyy-MM-dd')
                : null,
        };

        if (user?.user_type !== "Merchant") {
            payload.admin_id = user?.id;
        } else {
            payload.merchant_id = user?.id;
        }

        delete payload.dateRange;
        setIsLoading(true);
        console.log('export payload', payload);
        commonApi.post('/merchant-subcategory/export', payload)
            .then((response) => {
                setIsLoading(false);
                setOpenAlert(true);
                if (response?.data?.s === 0) {
                    setAlertMessage(response?.data?.msg);
                    setSeverity('error');
                } else {
                    navigate('/download');
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error('Error fetching data:', error);
                setOpenAlert(true);
                setAlertMessage(error?.message || 'Something went wrong');
                setSeverity('error');
            });
    };

    useEffect(() => {
        onLoadApi();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage]);

    // You can provide any values or functions you want to make available to consumers here
    const contextValue = {
        onExport,
        locationState,
        setLocationState,
        filters,
        setFilters,
        initialFilters,
        viewDetail,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        selected,
        setSelected,
        isEdit,
        onLoadApi,
        isLoading,
        firstLoading,
        viewData,
        allSubCategories,
        allData,
        handleAddModal,
        handleCloseModal,
        setAddModal,
        addModal,
        setAllData,
        setAllSubCategories,
        setViewData,
        setFirstLoading,
        setIsLoading,
        setIsEdit,
        uploadModal,
        handleUploadModal,
        handleCloseUploadModal,
    };

    // Render the provider with children
    return <SubCategoryContext.Provider value={contextValue}>{children}</SubCategoryContext.Provider>;
}
